import React, { useState, useEffect, useMemo } from "react";

import DatePicker from "../../../Components/DatePicker";
import CommonInput from "../../../Components/Input";
import SuccessNotification from "../../../Components/SuccessNotification";
import TextArea from "../../../Components/TextArea";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  createTenant,
  CreateTenantPayload,
  clearCreateTenantState,
} from "../../../Store/Reducers/AdminSlice";
import FormModal from "../../FormModal";
import "./style.scss";

interface AddOrganizationModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess?: () => void;
}

interface OrganizationFormData {
  organizationName: string;
  urlSlug: string;
  domain: string;
  adminName: string;
  adminEmail: string;
  phoneNumber?: string;
  city?: string;
  country?: string;
  joinDate?: string;
  description?: string;
}

type OrganizationFormErrors = Partial<
  Record<keyof OrganizationFormData, string>
>;
type OrganizationTouchedFields = Partial<
  Record<keyof OrganizationFormData, boolean>
>;

const AddOrganizationModal: React.FC<AddOrganizationModalProps> = ({
  isOpen,
  toggle,
  onSuccess,
}) => {
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 50);
  };

  const dispatch = useAppDispatch();
  const { createTenantLoading, createTenantSuccess } = useAppSelector(
    (state: any) => state.admin
  );

  const [formData, setFormData] = useState<OrganizationFormData>({
    organizationName: "",
    urlSlug: "",
    domain: "",
    adminName: "",
    adminEmail: "",
    phoneNumber: "",
    city: "",
    country: "",
    joinDate: "",
    description: "",
  });

  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [touchedFields, setTouchedFields] = useState<OrganizationTouchedFields>(
    {}
  );

  const getValidationErrors = (
    currentFormData: OrganizationFormData
  ): OrganizationFormErrors => {
    const errors: OrganizationFormErrors = {};
    const organizationName = currentFormData.organizationName.trim();
    const slug = currentFormData.urlSlug.trim();
    const domain = currentFormData.domain.trim();
    const adminName = currentFormData.adminName.trim();
    const adminEmail = currentFormData.adminEmail.trim();
    const description = currentFormData.description?.trim() || "";

    if (!organizationName) {
      errors.organizationName = "Organization Name is required.";
    } else if (!/^[A-Za-z0-9_.,@ ]{1,100}$/.test(organizationName)) {
      errors.organizationName =
        "Use up to 100 chars: letters, numbers, spaces, and _ . , @";
    }

    if (!slug) {
      errors.urlSlug = "URL Slug is required.";
    } else if (!/^[a-z0-9-]{1,50}$/.test(slug)) {
      errors.urlSlug =
        "Use up to 50 chars: lowercase letters, numbers, and hyphens.";
    }

    if (!domain) {
      errors.domain = "Domain is required.";
    } else if (
      !/^(?=.{1,50}$)(?!-)(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/.test(domain)
    ) {
      errors.domain =
        "Enter a valid domain (for example: acme.com), up to 50 chars.";
    }

    if (!adminName) {
      errors.adminName = "Admin Name is required.";
    } else if (!/^[A-Za-z0-9 ]{1,50}$/.test(adminName)) {
      errors.adminName =
        "Use up to 50 chars: letters, numbers, and spaces only.";
    }

    if (!adminEmail) {
      errors.adminEmail = "Admin Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) {
      errors.adminEmail = "Please enter a valid email address.";
    }

    if (description.length > 300) {
      errors.description = "Description must be 300 characters or fewer.";
    }

    return errors;
  };

  const validationErrors = useMemo(
    () => getValidationErrors(formData),
    [formData]
  );
  const isSubmitDisabled =
    createTenantLoading || Object.keys(validationErrors).length > 0;
  const getFieldError = (field: keyof OrganizationFormData) =>
    touchedFields[field] ? validationErrors[field] : undefined;

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        organizationName: "",
        urlSlug: "",
        domain: "",
        adminName: "",
        adminEmail: "",
        phoneNumber: "",
        city: "",
        country: "",
        joinDate: "",
        description: "",
      });
      setShowSuccessNotification(false);
      setTouchedFields({});
      dispatch(clearCreateTenantState());
    }
  }, [isOpen, dispatch]);

  // Handle success state
  useEffect(() => {
    if (createTenantSuccess && isOpen) {
      setShowSuccessNotification(true);
      setTimeout(() => {
        toggle();
      }, 2000);
    }
  }, [createTenantSuccess, isOpen, dispatch, toggle, onSuccess]);

  useEffect(() => {
    if (createTenantSuccess) {
      onSuccess?.();
    }
  }, [createTenantSuccess]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof OrganizationFormData;
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));

    if (name === "organizationName") {
      const generatedSlug = generateSlug(value);
      setFormData(prev => ({
        ...prev,
        organizationName: value,
        urlSlug: generatedSlug,
      }));
      return;
    }

    if (name === "urlSlug") {
      const sanitizedSlug = generateSlug(value);
      setFormData(prev => ({ ...prev, urlSlug: sanitizedSlug }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (Object.keys(validationErrors).length > 0) return;

    const organizationName = formData.organizationName.trim();
    const slug = formData.urlSlug.trim();
    const domain = formData.domain.trim();
    const adminName = formData.adminName.trim();
    const adminEmail = formData.adminEmail.trim();

    const payload: CreateTenantPayload = {
      organization_name: organizationName,
      slug,
      domain,
      admin_name: adminName,
      admin_email: adminEmail,
      ...(formData.phoneNumber?.trim() && {
        phone: formData.phoneNumber.trim(),
      }),
      ...(formData.city?.trim() && { city: formData.city.trim() }),
      ...(formData.country?.trim() && { country: formData.country.trim() }),
      ...(formData.joinDate && { join_date: formData.joinDate }),
      ...(formData.description?.trim() && {
        description: formData.description.trim(),
      }),
    };

    await dispatch(createTenant(payload) as any);
  };

  return (
    <>
      <FormModal
        isOpen={isOpen}
        toggle={toggle}
        title="Create New Organization"
        onSubmit={handleSubmit}
        submitLabel="Create Organization"
        loading={createTenantLoading}
        disabled={isSubmitDisabled}
      >
        <div className="add-org-modal">
          <CommonInput
            label="Organization Name"
            name="organizationName"
            value={formData.organizationName}
            onChange={handleChange}
            placeholder="Acme Corporation"
            required
            infoText={
              getFieldError("organizationName")
                ? undefined
                : "Alphanumeric characters and special characters (_.,@) only, max 100 characters"
            }
            error={getFieldError("organizationName")}
          />

          <CommonInput
            label="URL Slug"
            name="urlSlug"
            value={formData.urlSlug}
            onChange={handleChange}
            placeholder="acme-corporation"
            required
            infoText={
              getFieldError("urlSlug")
                ? undefined
                : "URL: Alphanumeric characters and hyphens only, max 50 characters"
            }
            error={getFieldError("urlSlug")}
          />

          <CommonInput
            label="Domain"
            name="domain"
            value={formData.domain}
            onChange={handleChange}
            placeholder="acme.com"
            required
            infoText={
              getFieldError("domain")
                ? undefined
                : "Alphanumeric characters and dots only, max 50 characters"
            }
            error={getFieldError("domain")}
          />

          <CommonInput
            label="Admin Name"
            name="adminName"
            value={formData.adminName}
            onChange={handleChange}
            placeholder="John Doe"
            required
            infoText={
              getFieldError("adminName")
                ? undefined
                : "Alphanumeric characters only, max 50 characters"
            }
            error={getFieldError("adminName")}
          />

          <CommonInput
            label="Admin Email"
            name="adminEmail"
            type="email"
            value={formData.adminEmail}
            onChange={handleChange}
            placeholder="admin@acme.com"
            required
            infoText={
              getFieldError("adminEmail")
                ? undefined
                : "Must be a valid email address"
            }
            error={getFieldError("adminEmail")}
          />

          <div className="optional-section">
            <h6>Optional Admin Details</h6>

            <CommonInput
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber || ""}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
            />

            <div className="flex flex-wrap-fields gap-4">
              <CommonInput
                label="City"
                name="city"
                value={formData.city || ""}
                onChange={handleChange}
                placeholder="San Francisco"
                className="w-full"
              />
              <CommonInput
                label="Country"
                name="country"
                value={formData.country || ""}
                onChange={handleChange}
                placeholder="USA"
                className="w-full"
              />
            </div>

            <DatePicker
              label="Join Date"
              name="joinDate"
              value={formData.joinDate || ""}
              onChange={handleChange}
              placeholder="mm/dd/yyyy"
            />

            <TextArea
              label="Description"
              name="description"
              value={formData.description || ""}
              onChange={handleChange}
              placeholder="Brief description of the organization..."
              rows={4}
              infoText={
                getFieldError("description")
                  ? undefined
                  : "Alphanumeric characters and special characters, max 300 characters"
              }
              error={getFieldError("description")}
            />
          </div>
        </div>
      </FormModal>
      <SuccessNotification
        isOpen={showSuccessNotification}
        onClose={() => setShowSuccessNotification(false)}
        message="Organization Created Successfully"
        details="The organization has been created successfully and is now available in the organizations list."
        type="success"
      />
    </>
  );
};

export default AddOrganizationModal;
